import { apiErrorCopy, hasApiErrorCopy } from "./api-error-copy";

const IDEMPOTENCY_ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789_-";

function stableJson(value: unknown): string {
  if (value === null || typeof value !== "object") return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(stableJson).join(",")}]`;
  const record = value as Record<string, unknown>;
  return `{${Object.keys(record).sort().filter((key) => record[key] !== undefined)
    .map((key) => `${JSON.stringify(key)}:${stableJson(record[key])}`).join(",")}}`;
}

export function createIdempotencyKey(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(32));
  return Array.from(bytes, (byte) => IDEMPOTENCY_ALPHABET[byte % IDEMPOTENCY_ALPHABET.length]).join("");
}

export class DeliberateActionAttempt {
  private signature: string | null = null;
  private key: string | null = null;
  private settled = false;

  keyFor(normalizedNonSecretPayload: unknown): string {
    const signature = stableJson(normalizedNonSecretPayload);
    if (this.signature !== signature || this.settled || !this.key) {
      this.signature = signature;
      this.key = createIdempotencyKey();
      this.settled = false;
    }
    return this.key;
  }

  settle(): void {
    this.settled = true;
  }

  invalidate(): void {
    this.signature = null;
    this.key = null;
    this.settled = false;
  }
}

export const WAITING_POOL_ERROR_COPY: Record<string, string> = {
  profile_draft_conflict: "资料草稿已更新。已加载最新版本，刚才的修改没有保存，请核对后重试。",
  profile_revision_conflict: "展示资料已更新，请重新预览。",
  preferences_conflict: "匹配偏好已更新，请刷新后再保存。",
  preferences_revision_conflict: "匹配偏好已更新，请重新预览。",
  revision_conflict: "这项设置已更新，请刷新后再继续。",
  participation_conflict: "参与状态已更新，请刷新后再继续。",
  pairing_action_conflict: "这次相遇的状态已改变，已刷新为当前可见的内容。",
  pairing_candidate_not_found: "这次相遇已无法继续操作，已刷新为当前可见的内容。",
  active_configuration_requires_choice: "按目前的设置，保存后需要暂停匹配。可以调整设置，或选择“保存并暂停”。",
  idempotency_key_reused: "这次操作的内容与上次请求不同，请刷新后重试。",
  credential_verifier_unavailable: "暂时无法验证身份，账户尚未删除，请稍后重试。",
  feature_closed: "Matching Pool 暂未开放，账户和已保存的内容都会保留。",
  maintenance_in_progress: "服务正在维护，当前更改尚未保存。请稍后再试。",
  production_safety_configuration_invalid: "暂时无法安全地完成操作，当前更改尚未保存，请稍后再试。",
};

export function waitingPoolErrorMessage(code: string, fallback: string): string {
  if (hasApiErrorCopy(fallback)) return apiErrorCopy(fallback);
  return WAITING_POOL_ERROR_COPY[code] ?? fallback;
}

export type RevisionConflictSection = "profile" | "preferences" | "location";

export const REVISION_CONFLICT_CODES = new Set([
  "profile_draft_conflict",
  "profile_revision_conflict",
  "preferences_conflict",
  "preferences_revision_conflict",
  "revision_conflict",
]);

export const REVISION_CONFLICT_COPY = {
  zh: "内容已更新。已加载服务器上的最新版本，刚才的修改没有保存。请核对后重新修改或提交。",
  en: "This content was updated in another window or on another device. We reloaded the latest version from the server; the changes you just submitted were not saved. Review the latest content before editing or submitting again.",
} as const;

const REVISION_CONFLICT_TITLES: Record<RevisionConflictSection, string> = {
  profile: "资料草稿已更新",
  preferences: "匹配偏好已更新",
  location: "地点设置已更新",
};

export function isRevisionConflictCode(code: string): boolean {
  return REVISION_CONFLICT_CODES.has(code);
}

export function revisionConflictNoticeModel(section: RevisionConflictSection) {
  const title = REVISION_CONFLICT_TITLES[section];
  return {
    role: "alert" as const,
    tabIndex: -1,
    title,
    message: REVISION_CONFLICT_COPY.zh,
    dismissLabel: `关闭${title}提示`,
  };
}

export function graphemeCount(value: string): number {
  return Array.from(new Intl.Segmenter(undefined, { granularity: "grapheme" }).segment(value)).length;
}

export function participationStateHeading(
  status: string,
  uiState: string,
): string {
  if (status === "ended") return "已永久结束匹配";
  if (status === "paused") return "已暂停匹配";
  if (uiState === "action_required") return "有一项参与设置需要处理";
  if (uiState === "waiting") return "等待符合条件的人选";
  return "准备加入 Matching Pool";
}
